import type { DeliveryBundle, DeliveryState } from './deliveryState'

/** A downloaded bundle's files: name → address and size. */
export type BundleFiles = Readonly<
  Record<string, { readonly sha256: string; readonly size: number }>
>

/** Everything the web client persists for map delivery, under one key. */
export interface DeliveryRecord {
  readonly state: DeliveryState
  /** Files of every downloaded bundle the state names, by manifest. */
  readonly files: Readonly<Record<string, BundleFiles>>
  /** When the manifest was last checked (epoch ms) and its ETag. */
  readonly checkedAt: number | null
  readonly etag: string | null
  /** Failed download attempts in a row — the retry back-off; absent = 0. */
  readonly retryAttempts?: number
  /** The manifest id of a download not yet staged; absent = none. */
  readonly downloading?: string | null
}

const KEY = 'metro.mapDelivery.v1'

function isBundle(v: unknown): v is DeliveryBundle {
  if (typeof v !== 'object' || v === null) return false
  const b = v as Record<string, unknown>
  return (
    (b.source === 'bundled' || b.source === 'downloaded') &&
    Number.isInteger(b.sequence) &&
    Number.isInteger(b.format) &&
    typeof b.manifest === 'string' &&
    typeof b.complete === 'boolean'
  )
}

function isOptionalBundle(v: unknown): boolean {
  return v === undefined || isBundle(v)
}

function isState(v: unknown): v is DeliveryState {
  if (typeof v !== 'object' || v === null) return false
  const s = v as Record<string, unknown>
  return (
    isBundle(s.bundled) &&
    isOptionalBundle(s.active) &&
    isOptionalBundle(s.lastGood) &&
    isOptionalBundle(s.pending) &&
    Array.isArray(s.onDisk) &&
    s.onDisk.every(isBundle) &&
    Array.isArray(s.bad) &&
    s.bad.every((m) => typeof m === 'string') &&
    typeof s.activeConfirmed === 'boolean' &&
    (s.marker === undefined || typeof s.marker === 'string') &&
    Number.isInteger(s.failures) &&
    (s.failedManifest === undefined || typeof s.failedManifest === 'string') &&
    Number.isInteger(s.failedCount)
  )
}

/**
 * The delivery record in `localStorage`. A missing, unreadable or malformed
 * record reads as `null` — the client then starts over from the bundled map
 * (spec §7 row 4: a corrupt pointer never breaks the map).
 */
export class DeliveryStore {
  private readonly storage: Pick<Storage, 'getItem' | 'setItem'>

  constructor(storage: Pick<Storage, 'getItem' | 'setItem'>) {
    this.storage = storage
  }

  load(): DeliveryRecord | null {
    let raw: string | null
    try {
      raw = this.storage.getItem(KEY)
    } catch {
      return null
    }
    if (raw === null) return null
    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      return null
    }
    if (typeof parsed !== 'object' || parsed === null) return null
    const r = parsed as Record<string, unknown>
    if (!isState(r.state) || typeof r.files !== 'object' || r.files === null) return null
    return {
      state: r.state,
      files: r.files as DeliveryRecord['files'],
      checkedAt: typeof r.checkedAt === 'number' ? r.checkedAt : null,
      etag: typeof r.etag === 'string' ? r.etag : null,
      retryAttempts: Number.isInteger(r.retryAttempts) ? (r.retryAttempts as number) : 0,
      downloading: typeof r.downloading === 'string' ? r.downloading : null,
    }
  }

  /** Writes the whole record in one `setItem` — the pointer update is atomic. */
  save(record: DeliveryRecord): boolean {
    try {
      this.storage.setItem(KEY, JSON.stringify(record))
      return true
    } catch {
      return false
    }
  }
}
